'use client'

import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { dataTestIds } from 'product-card/constants/dataTestIds'
import { ProductCardHoverProvider } from 'product-card/context/ProductCardHoverContext'
import { ProductCardRefProvider } from 'product-card/context/ProductCardRefContext'
import { useProductCardContext } from 'product-card/hooks/useProductCardContext'
import { ProductCardVariant } from 'product-card/types'
import { useProductInfo } from 'product/services/productInfo/client'
import type { Product, ProductColor, ProductImageType } from 'product/types'
import type { ProductPrices } from 'product/types/Prices'
import {
	type Dispatch,
	type SetStateAction,
	useEffect,
	useMemo,
	useRef,
} from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { cx } from 'utils/cx'

import { useProductCardConsumerLayoutContext } from '../../hooks/useProductCardConsumerLayoutContext'
import { useProductColors } from '../../hooks/useProductColors'
import { ProductCardActionButtons } from './ProductCardActionButtons'
import { ProductDetails } from './ProductDetails'
import { ProductImageContainer } from './ProductImageContainer'

import styles from './ProductCard.module.scss'

interface ProductCardBaseProps {
	product: Product | undefined
	imageSortCriteria: ProductImageType[]
	prices?: ProductPrices // This property allows us to manage the call for product pricing from the client or server as needed.
	isProductLoading?: boolean
	priority?: boolean
	isTwoColumnSize?: boolean
	className?: string
	setShouldFetch?: Dispatch<SetStateAction<boolean>>
	showDiscount?: boolean
}

function getShowAsFeatured(
	shouldRenderFeatured: boolean,
	isLargeOrGreater: boolean,
	isTwoColumnSize = false
) {
	return !!isTwoColumnSize && shouldRenderFeatured && isLargeOrGreater
}

function isReadyToRender(
	haveProductLooks: boolean,
	isIntersecting = false,
	priority = false
) {
	return haveProductLooks && (isIntersecting || priority)
}

function getHaveProductLooks(selectedColor: ProductColor | undefined) {
	return !!selectedColor?.looks && Object.keys(selectedColor.looks).length > 0
}

export const ProductCard = ({
	product,
	imageSortCriteria,
	priority,
	prices,
	isTwoColumnSize,
	isProductLoading = false,
	className,
	setShouldFetch,
	showDiscount,
}: ProductCardBaseProps) => {
	const {
		country: { countryISO, languageISO },
	} = useMasterData()
	const { isLargeOrGreater } = useResponsive()
	const productCardRef = useRef<HTMLDivElement | null>(null)
	const { showActionIcon, variant, shouldRenderFeatured } =
		useProductCardConsumerLayoutContext()
	const { productId, shouldFetchProduct, dataTestId, setIcon } =
		useProductCardContext()

	useEffect(() => {
		setIcon(showActionIcon)
	}, [showActionIcon, setIcon])

	const productCardEntry = useIntersectionObserver(productCardRef, {
		threshold: 0,
		rootMargin: `0px 0px 150px 0px`,
		freezeOnceVisible: true,
	})

	/**
	 * Get Product Data.
	 */
	const { data: productData } = useProductInfo({
		productId,
		country: countryISO,
		language: languageISO,
		enabled: shouldFetchProduct,
	})

	const currentProduct = useMemo(
		() => productData ?? product,
		[productData, product]
	)

	/**
	 * Get Product Image Data.
	 */
	const { selectedColor } = useProductColors({ product: currentProduct })
	const haveProductLooks = getHaveProductLooks(selectedColor)

	/**
	 * This product has been marked to span two columns, this means that is a featured product on large screen devices.
	 */
	const showAsFeatured = getShowAsFeatured(
		shouldRenderFeatured,
		isLargeOrGreater,
		isTwoColumnSize
	)

	useEffect(() => {
		if (productCardEntry?.isIntersecting && setShouldFetch) {
			setShouldFetch(true)
		}
	}, [productCardEntry?.isIntersecting])

	const readyToShowCard = isReadyToRender(
		haveProductLooks,
		productCardEntry?.isIntersecting,
		priority
	)

	return (
		<div
			className={cx(
				styles.productCard,
				className,
				isProductLoading && styles.empty
			)}
			data-testid={dataTestId}
			ref={productCardRef}
		>
			{currentProduct && readyToShowCard ? (
				<ProductCardRefProvider initialProductCardRef={productCardRef}>
					<div
						data-testid={
							showAsFeatured
								? dataTestIds.productCardFeatured(dataTestId)
								: undefined
						}
					>
						<ProductCardHoverProvider>
							<ProductImageContainer
								productId={productId}
								product={currentProduct}
								imageSortCriteria={imageSortCriteria}
								priority={priority}
								showAsFeatured={showAsFeatured}
							/>
						</ProductCardHoverProvider>
						{variant === ProductCardVariant.General && (
							<ProductDetails
								productId={productId}
								product={currentProduct}
								prices={prices}
								showAsFeatured={showAsFeatured}
								showDiscount={showDiscount}
							/>
						)}
					</div>
					{variant === ProductCardVariant.General && (
						<ProductCardActionButtons product={currentProduct} />
					)}
				</ProductCardRefProvider>
			) : (
				<div className={styles.empty} />
			)}
		</div>
	)
}
